import { useMemo } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { computeBreakdown, formatMoney } from "@/lib/calculations";
import { convert } from "@/lib/fx";
import { getCurrencySymbol } from "@/lib/constants";

export default function Spreadsheet({
  template,
  ratesObj,
  activeProductId,
  onActivateProduct,
  onUpdateProduct,
  onAddProduct,
  onRemoveProduct,
}) {
  const fromCcy = template.inputCurrency;
  const toCcy = template.displayCurrency;
  const fromSymbol = getCurrencySymbol(fromCcy);
  const toSymbol = getCurrencySymbol(toCcy);

  const rows = useMemo(
    () =>
      template.products.map((p) => ({
        product: p,
        breakdown: computeBreakdown(p, template.costLines, template.margin),
      })),
    [template]
  );

  const conv = (v) => convert(v, fromCcy, toCcy, ratesObj);

  const totals = useMemo(() => {
    let landed = 0;
    let selling = 0;
    let margin = 0;
    for (const r of rows) {
      landed += r.breakdown.totalLandedCost;
      selling += r.breakdown.totalSellingPrice;
      margin += r.breakdown.marginAmount * r.breakdown.quantity;
    }
    return {
      landed: conv(landed),
      selling: conv(selling),
      margin: conv(margin),
    };
  }, [rows, ratesObj, fromCcy, toCcy]);

  return (
    <div className="flex-1 overflow-auto" data-testid="spreadsheet">
      <div className="min-w-[1100px]">
        <table className="w-full border-collapse text-sm bg-white">
          <thead className="sticky top-0 z-10">
            <tr className="bg-neutral-100 border-b-2 border-neutral-900">
              <th className="px-3 py-2 text-left text-[10px] font-semibold tracking-[0.12em] uppercase text-neutral-600 w-10 border-r border-neutral-300">
                #
              </th>
              <th className="px-3 py-2 text-left text-[10px] font-semibold tracking-[0.12em] uppercase text-neutral-600 min-w-[200px] border-r border-neutral-300">
                Product
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold tracking-[0.12em] uppercase text-neutral-600 w-20 border-r border-neutral-300">
                Qty
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold tracking-[0.12em] uppercase text-neutral-600 w-32 border-r border-neutral-300">
                Cost ({fromCcy})
              </th>
              {template.costLines.map((l) => (
                <th
                  key={l.id}
                  className="px-3 py-2 text-right text-[10px] font-semibold tracking-[0.12em] uppercase text-neutral-600 w-32 border-r border-neutral-300"
                >
                  {l.label}
                  <span className="block text-[9px] font-mono normal-case text-neutral-400">
                    {l.type === "percent" ? `${l.value}%` : `flat ${fromSymbol}`}
                  </span>
                </th>
              ))}
              <th className="px-3 py-2 text-right text-[10px] font-semibold tracking-[0.12em] uppercase text-neutral-600 w-32 border-r border-neutral-300 bg-neutral-200">
                Landed ({toCcy})
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold tracking-[0.12em] uppercase text-neutral-600 w-32 border-r border-neutral-300">
                Margin
              </th>
              <th
                className="px-3 py-2 text-right text-[10px] font-semibold tracking-[0.12em] uppercase border-r border-neutral-300 bg-[#002FA7] text-white"
              >
                Selling/Unit ({toCcy})
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold tracking-[0.12em] uppercase text-neutral-600 w-32 border-r border-neutral-300">
                Total
              </th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const p = r.product;
              const b = r.breakdown;
              const isActive = p.id === activeProductId;
              return (
                <tr
                  key={p.id}
                  data-testid={`product-row-${p.id}`}
                  onClick={() => onActivateProduct(p.id)}
                  className={`border-b border-neutral-200 hover:bg-neutral-50 cursor-pointer transition-colors ${
                    isActive ? "row-active" : ""
                  }`}
                >
                  <td className="px-3 py-1 text-xs font-mono text-neutral-400 border-r border-neutral-200 text-center">
                    {idx + 1}
                  </td>
                  <td className="border-r border-neutral-200">
                    <input
                      data-testid={`product-name-input-${p.id}`}
                      value={p.name}
                      onChange={(e) =>
                        onUpdateProduct(p.id, { ...p, name: e.target.value })
                      }
                      placeholder="e.g. Toy Bus"
                      className="w-full px-3 py-2 bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-[#002FA7] focus:z-10 text-sm"
                    />
                  </td>
                  <td className="border-r border-neutral-200">
                    <input
                      type="number"
                      min={0}
                      step="1"
                      data-testid={`product-qty-input-${p.id}`}
                      value={p.quantity}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const parsed = raw === "" ? 0 : parseInt(raw, 10);
                        onUpdateProduct(p.id, {
                          ...p,
                          quantity: isNaN(parsed) ? 0 : Math.max(0, parsed),
                        });
                      }}
                      className="w-full px-3 py-2 bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-[#002FA7] focus:z-10 text-right font-mono text-sm"
                    />
                  </td>
                  <td className="border-r border-neutral-200">
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      data-testid={`product-cost-input-${p.id}`}
                      value={p.cost}
                      onChange={(e) =>
                        onUpdateProduct(p.id, {
                          ...p,
                          cost: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-[#002FA7] focus:z-10 text-right font-mono text-sm"
                    />
                  </td>
                  {b.lines.map((l) => (
                    <td
                      key={l.id}
                      className="px-3 py-2 text-right font-mono text-sm text-neutral-700 border-r border-neutral-200"
                      data-testid={`line-${l.id}-cell-${p.id}`}
                    >
                      {formatMoney(conv(l.amount), toSymbol)}
                    </td>
                  ))}
                  <td
                    className="px-3 py-2 text-right font-mono text-sm font-semibold text-neutral-900 border-r border-neutral-200 bg-neutral-50"
                    data-testid={`landed-cost-cell-${p.id}`}
                  >
                    {formatMoney(conv(b.totalCostBeforeMargin), toSymbol)}
                  </td>
                  <td
                    className="px-3 py-2 text-right font-mono text-sm text-emerald-700 border-r border-neutral-200"
                    data-testid={`margin-cell-${p.id}`}
                  >
                    {formatMoney(conv(b.marginAmount), toSymbol)}
                  </td>
                  <td
                    className="px-3 py-2 text-right font-mono text-sm font-bold text-[#002FA7] border-r border-neutral-200 bg-[#F0F4FF]"
                    data-testid={`selling-price-cell-${p.id}`}
                  >
                    {formatMoney(conv(b.sellingPricePerUnit), toSymbol)}
                  </td>
                  <td
                    className="px-3 py-2 text-right font-mono text-sm font-semibold text-neutral-900 border-r border-neutral-200"
                    data-testid={`total-cell-${p.id}`}
                  >
                    {formatMoney(conv(b.totalSellingPrice), toSymbol)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      data-testid={`remove-product-${p.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveProduct(p.id);
                      }}
                      className="text-neutral-300 hover:text-red-600 transition-colors"
                      aria-label="Remove product"
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={6 + template.costLines.length}
                  className="px-3 py-12 text-center text-sm text-neutral-400"
                >
                  No products yet. Click &quot;Add product&quot; below.
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="sticky bottom-0">
              <tr className="bg-neutral-900 text-white">
                <td colSpan={4 + template.costLines.length} className="px-3 py-3 text-[10px] font-semibold tracking-[0.18em] uppercase text-right border-r border-neutral-700">
                  Totals
                </td>
                <td className="px-3 py-3 text-right font-mono font-semibold text-sm border-r border-neutral-700" data-testid="totals-landed">
                  {formatMoney(totals.landed, toSymbol)}
                </td>
                <td className="px-3 py-3 text-right font-mono text-sm text-emerald-400 border-r border-neutral-700" data-testid="totals-margin">
                  {formatMoney(totals.margin, toSymbol)}
                </td>
                <td className="px-3 py-3 text-right font-mono text-sm border-r border-neutral-700">—</td>
                <td className="px-3 py-3 text-right font-mono font-bold text-base border-r border-neutral-700" data-testid="totals-selling">
                  {formatMoney(totals.selling, toSymbol)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="p-4 border-t border-neutral-200 bg-white">
        <Button
          onClick={onAddProduct}
          data-testid="add-product-button"
          className="rounded-none bg-white border border-neutral-900 text-neutral-900 hover:bg-neutral-100 gap-2 h-9"
        >
          <Plus size={14} weight="bold" /> Add product
        </Button>
      </div>
    </div>
  );
}
